<p-splitter [panelSizes]="[83, 17]"
            [minSizes]="[25,17]"
            stateKey="brighthub-copilot-chat-splitter"
            stateStorage="local"
            (onResizeEnd)="scrollToBottom()"
            layout="vertical"
            styleClass="chat-splitter chat-splitter-fill"
            [style]="{ height: '100%' }">
  <!-- Top panel: Chat messages -->
  <ng-template #panel>
    <div class="chat-scroll-container">
      @if (showConnectorStatus()) {
        <lib-connector-status
          [agentIdentifier]="selectedAgentIdentifier()"
        />
      }
      <div
        class="chat-search-floating"
        [class.chat-search-floating--expanded]="searchExpanded()"
      >
        @if (!searchExpanded()) {
          <button
            type="button"
            class="chat-search-toggle"
            (click)="toggleSearchExpanded()"
            aria-label="Search messages"
          >
            <i class="pi pi-search" aria-hidden="true"></i>
          </button>
        } @else {
          <div
            class="chat-search-panel"
            role="search"
            title="After focusing the message list: ⌘F or Ctrl+F opens search. Arrow keys: between matches. Escape: close."
          >
            <input
              #chatSearchInput
              type="search"
              class="chat-search-input"
              placeholder="Search in chat…"
              [value]="chatSearchDraft()"
              (input)="onChatSearchDraftInput($any($event.target).value)"
              (keydown.enter)="$event.preventDefault(); runChatSearch()"
              aria-label="Search messages"
            />
            <p-button
              type="button"
              label="Search"
              styleClass="p-button-sm p-button-outlined"
              (onClick)="runChatSearch()"
            />
            @if (chatSearchFromStore().query.trim()) {
              <span class="chat-search-count" aria-live="polite">
                @if (searchMatchSummary().total > 0) {
                  {{ searchMatchSummary().current }} / {{ searchMatchSummary().total }}
                } @else {
                  0 matches
                }
              </span>
              @if (searchMatchSummary().total > 0) {
                <button
                  type="button"
                  class="chat-search-nav"
                  (click)="navigateChatSearchMatch(-1)"
                  aria-label="Previous match"
                >
                  <i class="pi pi-angle-left" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="chat-search-nav"
                  (click)="navigateChatSearchMatch(1)"
                  aria-label="Next match"
                >
                  <i class="pi pi-angle-right" aria-hidden="true"></i>
                </button>
              }
            }
            <button
              type="button"
              class="chat-search-close"
              (click)="closeSearchPanel()"
              aria-label="Close search"
            >
              <i class="pi pi-times" aria-hidden="true"></i>
            </button>
          </div>
        }
      </div>
      @if (showScrollToBottomButton() && !hideMessagesUntilScrolled()) {
        <button
          type="button"
          class="chat-scroll-to-bottom"
          (click)="scrollToBottom('smooth')"
          aria-label="Scroll to bottom"
        >
          <i class="pi pi-arrow-down" aria-hidden="true"></i>
        </button>
      }
      <bds-scroll-region
        class="chat-messages-scroll-region"
        styleClass="chat-messages-scroll"
        ariaLabel="Chat messages"
      >
        <div
          class="chat-messages"
          [class.chat-messages-before-scroll]="hideMessagesUntilScrolled()">
          @for (message of viewMessages$ | async; track message.id) {
            <lib-chat-message
              [message]="message"
              [markdownBody]="message.markdownBody"
              [copilotName]="copilotName()"
              [userDisplayName]="message.userDisplayName"
              [userInitials]="message.userInitials"
              [hideFeedback]="forceHideInput() || forceDisableInput()"
              [showThinkingBelow]="message.metadata?.['streamPaused'] === true"
              [wiggleAvatar]="message.role === 'assistant' && message.id === lastAssistantMessageId() && streamJustEnded()"
            />
          }
          @if (showThinking$ | async) {
            <div class="relative message assistant thinking">
              <div class="flex-shrink-0 w-8 h-8 rounded-full avatar assistant">
                <img
                  src="assets/copilot/brightside_bug.png"
                  [alt]="copilotName()"
                  class="w-full h-full rounded-full object-cover"
                />
              </div>
              <div class="flex gap-2 mb-1 text-xs text-surface-500 meta">
                <span class="font-semibold capitalize role">{{ copilotName() }}</span>
                <span class="timestamp">now</span>
              </div>
              <div class="italic text-surface-600 thinking-dots content">
                {{ copilotName() }} is thinking<span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>
              </div>
            </div>
          }
        </div>
      </bds-scroll-region>
    </div>
  </ng-template>

  <!-- Bottom panel: Chat input -->
@if(!forceHideInput()) {
    <ng-template #panel>
      @if (selectedConversationId$ | async; as selectedConversationId) {
        @if (!forceDisableInput()) {
            <form class="flex flex-col w-full h-full p-4 gap-2 chat-input" (ngSubmit)="send()">
            <lib-chat-suggestions-bar
              [suggestions]="chatSuggestions()"
              [chipsDisabled]="suggestionChipsDisabled()"
              (macroSelect)="applySuggestionMacro($event)"
            />
            @if (canContinueReply$ | async) {
              <div class="flex items-center justify-between gap-3 text-sm text-gray-700">
                <span>The reply was interrupted.</span>
                <p-button
                  type="button"
                  label="Continue reply"
                  styleClass="p-button-text p-button-sm"
                  (onClick)="continueReply()"
                />
              </div>
            }
            <div class="chat-input__composer">
            <textarea
              #messageInput
              [value]="inputText()"
              (input)="onInputChange($event)"
              name="message"
              placeholder="Type your message..."
              (keydown.enter)="handleEnter($event, selectedConversationId)"
              [disabled]="forceDisableInput() || !selectedConversationId"
              class="flex-1 min-h-[2.5rem] min-w-0 max-h-full p-2 border rounded resize-none overflow-auto"
            ></textarea>
              <div class="flex justify-end flex-shrink-0">
                <p-button
                  type="submit"
                  label="Send"
                  [disabled]="forceDisableInput() || (!inputText().trim() || !selectedConversationId || disableInput())"
                  styleClass="p-button-outlined"
                />
              </div>
            </div>
            </form>
        } @else {
          <div class="flex justify-center items-center">
            This conversation is archived. You cannot send messages here.
          </div>
        }
      } @else if (!forceDisableInput()) {
        <div class="flex flex-col w-full h-full p-4 gap-2 opacity-50 pointer-events-none chat-input">
        <div class="chat-input__composer">
        <textarea
          disabled
          class="flex-1 min-h-[2.5rem] min-w-0 max-h-full p-2 border rounded resize-none overflow-auto"
          placeholder="Select a conversation to start chatting..."
        ></textarea>
          <div class="flex justify-end flex-shrink-0">
            <p-button
              type="button"
              label="Send"
              disabled
              styleClass="p-button-outlined"
            />
          </div>
        </div>
        </div>
      }
    </ng-template>
  }
</p-splitter>
